/**
 * Preparación de la ejecución (doc. 02 §4): valida, compila el layout y congela una configuración
 * inmutable con huellas de layout, proceso, parámetros y demanda. Dos ejecuciones con las mismas
 * huellas y semilla dan resultados idénticos.
 */

import { hashTexto } from '../aleatoriedad/generador';
import type { Catalogo, Espacio } from '../dominio/espacio';
import {
  REPLICAS_INICIALES,
  VERSION_MOTOR,
  type ConfiguracionDeEjecucion,
  type EstacionCompilada,
  type PersonaCompilada,
} from '../dominio/ejecucion';
import type { Observacion } from '../dominio/observacion';
import { ROLES } from '../dominio/proceso';
import { SEGUNDOS_POR_MINUTO, horaASegundos, instante } from '../dominio/tiempo';
import type { ModeloEspacial } from '../espacial/compilador';
import { validarEspacio } from '../validacion/espacio';

/** JSON con claves ordenadas: la misma información da siempre el mismo texto. */
export function jsonCanonico(valor: unknown): string {
  if (valor === null || typeof valor !== 'object') return JSON.stringify(valor) ?? 'null';
  if (Array.isArray(valor)) return `[${valor.map(jsonCanonico).join(',')}]`;
  const o = valor as Record<string, unknown>;
  return `{${Object.keys(o)
    .filter((k) => o[k] !== undefined)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${jsonCanonico(o[k])}`)
    .join(',')}}`;
}

/** Huella de 64 bits (16 caracteres hexadecimales) de cualquier valor serializable. */
export function huellaDe(valor: unknown): string {
  const texto = jsonCanonico(valor);
  const a = hashTexto(texto, 0x2545f491);
  const b = hashTexto(texto, 0x9e3779b9);
  return a.toString(16).padStart(8, '0') + b.toString(16).padStart(8, '0');
}

export type Preparacion =
  | {
      ok: true;
      config: ConfiguracionDeEjecucion;
      modelo: ModeloEspacial;
      observaciones: Observacion[];
    }
  | { ok: false; observaciones: Observacion[]; modelo: ModeloEspacial };

export function prepararEjecucion(espacio: Espacio, catalogo: Catalogo): Preparacion {
  const v = validarEspacio(espacio, catalogo);
  if (v.bloquea) return { ok: false, observaciones: v.observaciones, modelo: v.modelo };
  const m = espacio.modelo;
  const modelo = v.modelo;

  const estaciones: EstacionCompilada[] = modelo.estaciones.map((s) => ({
    id: s.id,
    tipo: s.tipo,
    capacidad: s.capacidad,
    compatibles: s.compatibles,
    especializada: s.especializada,
    sigueTurno: s.sigueTurno,
    x: s.centro.x,
    z: s.centro.z,
    rotacion: s.rotacion,
    elementoId: s.elementoId,
  }));
  const personas: PersonaCompilada[] = [];
  for (const rol of ROLES) {
    const letra = rol === 'asesor' ? 'A' : 'T';
    for (let i = 1; i <= (m.dotacion[rol]?.valor ?? 0); i++) {
      personas.push({ id: `${letra}${String(i).padStart(2, '0')}`, rol });
    }
  }
  const demanda = {
    modo: m.demanda.modo,
    unidadesPorDia: m.demanda.unidadesPorDia.valor,
    perfilHorario: m.demanda.perfilHorario.valor,
    mezcla: m.demanda.mezcla.valor,
    inicioCitas: horaASegundos(m.jornada.inicioCitas.valor),
    ultimaCita: horaASegundos(m.jornada.ultimaCita.valor),
    agenda:
      m.demanda.modo === 'agenda'
        ? (m.demanda.agenda ?? []).map((c) => ({
            t: instante(c.dia, horaASegundos(c.hora)),
            tipoUnidad: c.tipoUnidad,
            ...(c.atributos ? { atributos: c.atributos } : {}),
          }))
        : [],
  };
  const traslados: ConfiguracionDeEjecucion['traslados'] =
    m.movimiento.modo === 'fijo'
      ? {
          modo: 'fijo',
          duracionS: Math.round(m.movimiento.trasladoFijoMin.valor * SEGUNDOS_POR_MINUTO),
        }
      : {
          modo: 'distancia',
          velocidadMs: m.movimiento.velocidadVehiculoKmh.valor / 3.6,
          maniobraS: m.movimiento.maniobraS.valor,
          distancias: modelo.distancias.map((fila) =>
            fila.map((d) => (d === null ? null : Math.round(d * 100) / 100)),
          ),
        };
  // Refrigerio de los técnicos (paso 1.2); sin él, no entra en la huella (las anteriores no cambian).
  const r = m.refrigerio;
  const refrigerio =
    r && r.duracionMin.valor > 0
      ? {
          inicio: horaASegundos(r.inicio.valor),
          duracionS: Math.round(r.duracionMin.valor * SEGUNDOS_POR_MINUTO),
          modo: r.modo.valor,
        }
      : null;
  const parametros = {
    personas,
    turno: { inicio: m.jornada.inicioTurno.valor, fin: m.jornada.finTurno.valor },
    cierre: m.jornada.cierre.valor,
    atencion: { inicio: m.atencion.inicio.valor, fin: m.atencion.fin.valor },
    redondeo: m.redondeoPromesaMin.valor,
    traslados: {
      modo: m.movimiento.modo,
      v: m.movimiento.velocidadVehiculoKmh.valor,
      man: m.movimiento.maniobraS.valor,
      fijo: m.movimiento.trasladoFijoMin.valor,
    },
    politicas: m.politicas,
    dias: espacio.configuracionAleatoria.dias,
    refrigerio: refrigerio ?? undefined,
  };
  // El origen (supuesto/ingresado) y el supuesto guardado para «volver al supuesto» no alteran
  // resultados: no entran en las huellas.
  const sinOrigen = (x: unknown) =>
    JSON.parse(
      JSON.stringify(x, (k, val) =>
        k === 'origen' || k === 'nota' || k === 'supuesto' ? undefined : val,
      ),
    ) as unknown;
  const huellas = {
    layout: huellaDe({
      estaciones,
      distancias: traslados.modo === 'distancia' ? traslados.distancias : null,
    }),
    proceso: huellaDe(sinOrigen(m.proceso)),
    parametros: huellaDe(parametros),
    demanda: huellaDe(demanda),
    total: '',
  };
  huellas.total = huellaDe({
    ...huellas,
    semilla: espacio.configuracionAleatoria.semilla,
    versionMotor: VERSION_MOTOR,
  });

  const config: ConfiguracionDeEjecucion = {
    versionMotor: VERSION_MOTOR,
    ejecucionId: `ej-${huellas.total}`,
    huellas,
    proceso: m.proceso,
    estaciones,
    personas,
    turno: {
      inicio: horaASegundos(m.jornada.inicioTurno.valor),
      fin: horaASegundos(m.jornada.finTurno.valor),
    },
    cierre: horaASegundos(m.jornada.cierre.valor),
    atencion: {
      inicio: horaASegundos(m.atencion.inicio.valor),
      fin: horaASegundos(m.atencion.fin.valor),
    },
    redondeoPromesaS: Math.round(m.redondeoPromesaMin.valor * SEGUNDOS_POR_MINUTO),
    dias: espacio.configuracionAleatoria.dias,
    demanda,
    traslados,
    politicas: m.politicas,
    refrigerio,
    semilla: espacio.configuracionAleatoria.semilla,
    replicas: espacio.configuracionAleatoria.replicasAutomaticas
      ? REPLICAS_INICIALES
      : espacio.configuracionAleatoria.replicas,
    replicasAutomaticas: espacio.configuracionAleatoria.replicasAutomaticas,
  };
  return { ok: true, config, modelo, observaciones: v.observaciones };
}
